/**
 * The page a role is refused (release 3.1, persona D6; SPEC.md principle P9):
 * never a silent redirect to Home, never a page that fails with 403s. It says
 * why in plain words - the role it is not part of and who manages it, the
 * permission that is missing, or the licence - and links back to Home.
 *
 * `data-testid="role-refusal"` is the persona walk's hook (SPEC.md 9.1).
 */
import { Link } from '@tanstack/react-router';
import { ShieldOff } from 'lucide-react';

import {
  permissionInWords,
  ROLE_LABELS,
  rolesThatOpen,
  type PageDecision,
  type PageEntry,
} from '@/generated/persona/discovery-access';
import { Button } from '@/components/ui/button';
import { CANCELLED_SENTENCE, MODULE_LABELS, SUPPORT_EMAIL, planLabel } from '@/lib/plan-modules';
import { useEntitlements } from '@/lib/use-entitlements';

function listInWords(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The sentences the refusal shows, for a decision. Exported for tests. */
export function refusalCopy(
  page: PageEntry,
  decision: Extract<PageDecision, { allowed: false }>,
  role: string,
  plan: string | null = null,
  billingCancelled = false,
): { heading: string; lines: string[] } {
  const roleLabel = ROLE_LABELS[role] ?? role;
  const owners = rolesThatOpen(page);
  switch (decision.reason) {
    case 'role':
      return {
        heading: `This page is not part of the ${roleLabel} role.`,
        lines: owners.length
          ? [`${page.label} is for the ${listInWords(owners)} ${owners.length === 1 ? 'role' : 'roles'}.`]
          : [`${page.label} is not available to any role in this console.`],
      };
    case 'permission':
      return {
        heading: `Your role (${roleLabel}) does not have the permission this page needs.`,
        lines: [
          ...decision.missing.map((p) => `It needs permission to ${permissionInWords(p)} (${p}).`),
          ...(owners.length ? [`${page.label} is for the ${listInWords(owners)} ${owners.length === 1 ? 'role' : 'roles'}.`] : []),
        ],
      };
    case 'licence': {
      // Cancelled billing: no module is licensed. Say only that, never that
      // the plan lacks the module (the review at 2216dbe) nor that it comes
      // back on reinstatement (the review at 66b3b23: a plan that never had
      // it would not open it).
      if (billingCancelled) {
        return {
          heading: `${page.label} cannot be opened while your organisation's subscription is cancelled.`,
          lines: [CANCELLED_SENTENCE],
        };
      }
      const modules = listInWords(decision.missing.map((m) => MODULE_LABELS[m] ?? m));
      return {
        heading: `Your organisation's licence does not include ${modules}.`,
        lines: [`${page.label} opens once the module is licensed.${plan ? ` Your plan: ${plan}.` : ''} To add it, contact ${SUPPORT_EMAIL}.`],
      };
    }
    case 'licence_unknown':
      return {
        heading: "Your organisation's licences could not be checked, so this page stays closed.",
        lines: ['Try again in a moment. If it keeps happening, tell your IT administrator.'],
      };
  }
}

export function RoleRefusal({
  page,
  decision,
  role,
  onRetry,
}: {
  page: PageEntry;
  decision: Extract<PageDecision, { allowed: false }>;
  role: string;
  onRetry?: () => void;
}) {
  // The plan's name, for a licence refusal (UAT #215). Null while unknown.
  const { entitlements } = useEntitlements();
  const { heading, lines } = refusalCopy(
    page,
    decision,
    role,
    planLabel(entitlements?.sku),
    entitlements?.billingStatus === 'cancelled',
  );
  return (
    <div data-testid="role-refusal" data-page={page.id} data-refusal-reason={decision.reason} className="p-8 max-w-2xl">
      <div className="glass rounded-xl border border-border/40 p-6 space-y-4">
        <div className="flex items-center gap-3">
          <ShieldOff className="h-5 w-5 text-muted-foreground" aria-hidden />
          <h1 className="text-lg font-display font-semibold">{heading}</h1>
        </div>
        {lines.map((l) => (
          <p key={l} className="text-sm text-muted-foreground">
            {l}
          </p>
        ))}
        <div className="flex gap-2 pt-2">
          <Button asChild variant="default" size="sm">
            <Link to="/">Back to your Home</Link>
          </Button>
          {decision.reason === 'licence_unknown' && onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Shown when the caller's access could not be read at all (GET /v1/me failed). */
export function AccessUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <div data-testid="access-unavailable" className="p-8 max-w-2xl">
      <div className="glass rounded-xl border border-border/40 p-6 space-y-4">
        <h1 className="text-lg font-display font-semibold">Your access could not be checked.</h1>
        <p className="text-sm text-muted-foreground">
          Nothing is shown until it can be: pages open only for what your role is allowed. Try again in a moment.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  );
}
